import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { Store } from '@ngrx/store';

import {AppState} from "../../../shared/state/app.state";
import {
  loadBuildKits,
} from "../build-kit/build-kit.action";
import {catchError, map, switchMap} from "rxjs/operators";
import {of, tap} from "rxjs";
import {BuildKitSettingTemplateService} from "../../data-access/build-kit-setting-template.service";
import {
  addBuildKitSettingTemplate, addBuildKitSettingTemplateFailure, addBuildKitSettingTemplateSuccess,
  loadBuildKitSettingTemplatesFailure,
  loadBuildKitSettingTemplatesSuccess,
  modifyBuildKitSettingTemplate,
  modifyBuildKitSettingTemplateFailure,
  modifyBuildKitSettingTemplateSuccess,
  removeBuildKitSettingTemplate,
  removeBuildKitSettingTemplateFailure,
  removeBuildKitSettingTemplateSuccess,
  setSelectedBuildKitSettingTemplate,
  setSelectedBuildKitSettingTemplateFailure,
  setSelectedBuildKitSettingTemplateSuccess
} from "./build-kit-setting-template.action";
import {selectAllBuildKitSettingTemplates} from "./build-kit-setting-template.selectors";
import {ToastMessageOptions, MessageService} from "primeng/api";
import {GetMessage} from "../../../global-config/utils/toast-messages.utils";

@Injectable()
export class BuildKitSettingTemplateEffects {
  loadBuildKitSettingTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBuildKits, modifyBuildKitSettingTemplateSuccess, addBuildKitSettingTemplateSuccess),
      switchMap(() =>
        this.buildKitSettingTemplateService.get()
          .pipe(
            map((buildKitSettingTemplates) => loadBuildKitSettingTemplatesSuccess({buildKitSettingTemplates: buildKitSettingTemplates})),
            catchError((error) => of(loadBuildKitSettingTemplatesFailure({ error: GetMessage('LoadFailure', 'Build Kit Setting Templates') })))
          )
      )
    )
  );

  createBuildKitSettingTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addBuildKitSettingTemplate),
      switchMap((action) =>
        this.buildKitSettingTemplateService.create(action.buildKitSettingTemplate)
          .pipe(
            map((addedBuildKitSettingTemplate) => addBuildKitSettingTemplateSuccess({buildKitSettingTemplate: addedBuildKitSettingTemplate?.data, successMessage: GetMessage('AddSuccess', 'Build Kit Setting Template')})),
            catchError((error) => of(addBuildKitSettingTemplateFailure({ error: GetMessage('AddFailure', 'Build Kit Setting Template') })))
          )
      )
    )
  );

  modifyBuildKitSettingTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(modifyBuildKitSettingTemplate),
      switchMap((action) =>
        this.buildKitSettingTemplateService.update(action.buildKitSettingTemplate)
          .pipe(
            map((buildKitSettingTemplate) => modifyBuildKitSettingTemplateSuccess({buildKitSettingTemplate: buildKitSettingTemplate?.data, successMessage: GetMessage('ModifySuccess', 'Build Kit Setting Template')})),
            catchError((error) => of(modifyBuildKitSettingTemplateFailure({ error: GetMessage('ModifyFailure', 'Build Kit Setting Template') })))
          )
      )
    )
  );

  removeBuildKitSettingTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeBuildKitSettingTemplate),
      switchMap((action) =>
        this.buildKitSettingTemplateService.remove(action.buildKitSettingTemplateId)
          .pipe(
            map((removedBuildKitSettingTemplateId) => removeBuildKitSettingTemplateSuccess({buildKitSettingTemplateId: removedBuildKitSettingTemplateId, successMessage: GetMessage('RemoveSuccess', 'Build Kit Setting Template')})),
            catchError((error) => of(removeBuildKitSettingTemplateFailure({ error: GetMessage('RemoveFailure', 'Build Kit Setting Template') })))
          )
      )
    )
  );


  setSelectedBuildKitSettingTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSelectedBuildKitSettingTemplate),
      switchMap((action) =>
        this.store.select(selectAllBuildKitSettingTemplates)
          .pipe(
            map((buildKitSettingTemplates) =>
            {
              // if(action.selectedBuildKitSettingTemplateId === '0'){
              //   return setSelectedBuildKitSettingTemplateSuccess({ selectedBuildKitSettingTemplate: null })
              // }
              return setSelectedBuildKitSettingTemplateSuccess({ selectedBuildKitSettingTemplate: buildKitSettingTemplates.find(template => template.id == action.selectedBuildKitSettingTemplateId) ?? null })
            }
            ),
            catchError((error) => of(setSelectedBuildKitSettingTemplateFailure({ error })))
          )
      )
    )
  );

  toastSuccessMessages$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          addBuildKitSettingTemplateSuccess,
          modifyBuildKitSettingTemplateSuccess,
          removeBuildKitSettingTemplateSuccess
        ),
        tap(({ successMessage }) => {
            return this.messageService.add({
              severity: 'success',
              summary: successMessage,
            } as ToastMessageOptions)
          }
        )
      ),
    { dispatch: false }
  );

  toastErrorMessages$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          loadBuildKitSettingTemplatesFailure,
          addBuildKitSettingTemplateFailure,
          removeBuildKitSettingTemplateFailure,
          modifyBuildKitSettingTemplateFailure
        ),
        tap(({ error }) => {
            return this.messageService.add({
              severity: 'error',
              summary: error,
            } as ToastMessageOptions)
          }
        )
      ),
    { dispatch: false }
  );


  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private messageService: MessageService,
    private buildKitSettingTemplateService: BuildKitSettingTemplateService,
  ) {}
}

